import { API_BASE } from '../config/env';
import { httpConstants } from '../constants';
import { IdeationDataType } from '../features/NewIdeationXPLR/modules/Dashboard';
import { fieldAPIKeyMap, fieldConversationTypeMap } from '../features/NewIdeationXPLR/shared/constants';
import { httpService } from '../utility/httpService';

export const fetchIdeationData = async (
  page: number,
  size: number = 25,
  sortBy: string,
  status: string,
  searchTerm?: string
) => {
  const updatedStatus = status === 'all' || status === undefined ? '' : status;
  const organizationId = localStorage.getItem('current_organization') || '';
  const url = `${API_BASE}/api/v1/ideation/use-cases/by-organization/?organization_id=${organizationId}&page=${page}&size=${size}&sortBy=${sortBy}${!!updatedStatus ? `&status=${updatedStatus}` : ''}${!!searchTerm ? `&title_Icontains=${searchTerm}` : ''}`;
  const response = await httpService(httpConstants.METHOD_TYPE.GET, url);
  return response as { data: any; pagination: any };
};

export const fetchIdeationStatus = async (useCaseId: string) => {
  const organizationId = localStorage.getItem('current_organization') || '';
  const url = `${API_BASE}/api/v1/ideation/status/?organization_id=${organizationId}&use_case_id=${useCaseId}`;
  const response = await httpService(httpConstants.METHOD_TYPE.GET, url);
  return response as IdeationDataType;
};

export const getIdeationStatisticsTotal = async (organizationId: string) => {
  const url = `${API_BASE}/api/v1/ideation/statistics/total/?organization_id=${organizationId}`;
  const response = await httpService(httpConstants.METHOD_TYPE.GET, url);
  return response;
};

export const getIdeationStatisticsDomainAndFunction = async (
  organizationId: string,
  domain?: string,
  status?: string
) => {
  const url = `${API_BASE}/api/v1/ideation/statistics/?organization_id=${organizationId}${!!domain ? `&domain=${domain}` : ''}${!!status ? `&status=${status}` : ''}`;
  const response = await httpService(httpConstants.METHOD_TYPE.GET, url);
  return response;
};

export const getTableData = async (
  organizationId: string,
  page: number,
  size: number,
  status?: string,
  domain?: string,
  functionType?: string,
  impact?: string
) => {
  const url = `${API_BASE}/api/v1/ideation/use-cases/by-organization/?organization_id=${organizationId}&page=${page}&size=${size}&status=${status === 'all' || status === undefined ? '' : status}&domain=${domain === 'all' || domain === undefined ? '' : domain}&function=${functionType === 'All Functions' || functionType === undefined ? '' : functionType.replace('&', '%26')}&impact=${impact === 'all' || impact === undefined ? '' : impact}&`;
  const response = await httpService(httpConstants.METHOD_TYPE.GET, url, undefined, {
    'x-organization-id': organizationId,
  });
  return response;
};

export const deleteTableUsecase = async (id: string) => {
  const url = `${API_BASE}/api/v1/ideation/use-cases/${id}/soft_delete/`;
  const response = await httpService(httpConstants.METHOD_TYPE.DELETE, url);
  return response;
};

export const deleteTableNewAiOpportunity = async (id: string) => {
  const url = `${API_BASE}/api/v1/e2e_taxonomy/new_ai_opportunity/${id}/`;
  const response = await httpService(httpConstants.METHOD_TYPE.DELETE, url);
  return response;
};

export const getOpportunityData = async (
  organizationId: string,
  page: number,
  size: number,
  searchTerm?: string,
  functionName?: string,
  impact?: string,
  status?: string,
  sortBy?: string,
  sortOrder?: string,
): Promise<{ data: any; pagination: any }> => {
  const url = `${API_BASE}/api/v1/ideation/use-cases/ai-opportunities-by-organization/?organization_id=${organizationId}&page=${page}&size=${size}${!searchTerm ? '' : `&opportunityName_Icontains=${searchTerm}`}${!functionName ? '' : `&function=${encodeURIComponent(functionName)}`}${!impact ? '' : `&impact=${impact}`}${!status ? '' : `&status=${status}`}${!sortBy ? '' : `&sortBy=${sortBy}`}${!sortOrder ? '' : `&sortOrder=${sortOrder}`}`;
  const response = await httpService(httpConstants.METHOD_TYPE.GET, url, undefined, {
    'x-organization-id': organizationId,
  });
  return response as { data: any; pagination: any };
};

export const getOpportunityDetails = async (
  organizationId: string,
  diagramId: string,
  id: number
) => {
  const url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/with-agent-enablement/${id}/?diagram_id=${diagramId}&organization_id=${organizationId}`;
  const response = await httpService(httpConstants.METHOD_TYPE.GET, url);
  return response;
};

export const apiUpdateOpportunityDetails = async (id: number, payload: any) => {
  const url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/${id}/`;
  const response = await httpService(httpConstants.METHOD_TYPE.PATCH, url, payload);
  return response;
};

export const deleteQueryHistory = async (chatId: string, fieldType: string, conversation_type?: string) => {
  try {
    const organizationId = localStorage.getItem('current_organization') || '';
    const url = `${API_BASE}/api/v1/ideation/query-history/${chatId}/delete/`;
    const response = await httpService(httpConstants.METHOD_TYPE.DELETE, url, {
      organization_id: organizationId,
      field_type: fieldAPIKeyMap[fieldType],
      conversation_type: conversation_type || fieldConversationTypeMap[fieldType],
    });
    return response;
  } catch (error) {
    console.error('Failed to delete query history:', error);
    throw error;
  }
};

export const processStatusRollbackCheckIdeation = async (useCaseId: string) => {
  const url = `${API_BASE}/api/v1/ideation/rollback-check/`;
  return httpService(httpConstants.METHOD_TYPE.POST, url, { use_case_id: useCaseId });
};

export interface BulkOpportunityResponse {
  id: string;
  domain?: string;
  function?: string;
  process_group?: string;
  process?: string;
  expected_impact?: string;
  updated_at?: number;
  status?: string;
  [key: string]: unknown;
}

export interface FetchBulkOpportunitiesResponse {
  opportunities: BulkOpportunityResponse[];
}

/**
 * Fetches paginated opportunities for a bulk ideation.
 * Uses shared httpService (auth + org headers). Caller maps to UI shape if needed.
 */
export const fetchBulkOpportunities = async (
  bulkId: string,
  page: number = 1,
  size: number = 10
): Promise<FetchBulkOpportunitiesResponse> => {
  const url = `${API_BASE}/api/v1/ideation/bulk-opportunities/${bulkId}/opportunities/?page=${page}&size=${size}`;
  const response = await httpService<FetchBulkOpportunitiesResponse>(
    httpConstants.METHOD_TYPE.GET,
    url
  );
  return { opportunities: response?.opportunities ?? [] };
};

export const getUseCasesByLevel2Ids = async (ids: string[], organizationId: string) => {
  const url = `${API_BASE}/api/v1/ideation/use-cases/by-level2-ids/`;
  return await httpService(httpConstants.METHOD_TYPE.POST, url, { ids }, {
    'x-organization-id': organizationId,
  });
};

